<template>
  <v-container fluid class="pa-0">
    <v-row no-gutters align="center" class="toolbar-wrapper">
      <v-col cols="12" md="9" class="d-flex align-center flex-wrap ga-3">
        <div class="search-wrapper">
          <v-menu location="bottom start" offset="10">
            <template #activator="{ props }">
              <div class="search-type-selector" v-bind="props">
                <span>{{ selectedSearchField.label || "Search By" }}</span>
                <v-icon size="18">mdi-chevron-down</v-icon>
              </div>
            </template>

            <v-card min-width="420" rounded="xl" elevation="4" class="pa-4">
              <div class="text-subtitle-2 font-weight-bold mb-3">
                Search Options
              </div>

              <v-divider class="mb-3" /> 

              <v-radio-group v-model="selectedSearchField" hide-details density="compact">
                <v-row dense>
                  <v-col v-for="(option, index) in searchOptions" :key="index" cols="6">
                    <v-radio
                      :label="option.label"
                      :value="option"
                      density="compact"
                      color="warning"
                    />
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-card>
          </v-menu>

          <!-- Search Input -->
          <v-text-field
            v-model="searchValue"
            :placeholder="`Search by ${selectedSearchField.label || 'keyword'}`"
            variant="plain"
            density="compact"
            hide-details
            clearable
            class="search-input"
            @keyup.enter="applyFilters"
            @click:clear="handleReset"
          />

          <!-- Search Button -->
          <v-btn
            icon
            elevation="0"
            class="search-btn primary-gradient"
            @click="applyFilters"
          >
            <v-icon color="white">
              mdi-magnify
            </v-icon>
          </v-btn>
        </div>
          <!-- DATE FILTER -->
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          location="bottom"
          offset="4"
          transition="scale-transition"
          eager
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              class="mx-2"
              v-if="isDateAllowed()"
            >
              <v-icon v-if="!selectedDate">mdi-calendar-range</v-icon>
              <span v-else>{{ selectedDate }}</span>
            </v-btn>
          </template>

          <v-card>
            <v-date-picker
              v-model="selectedDate"
              @update:model-value="onDateSelect"
            />
          </v-card>
        </v-menu>
        <!-- FILTER -->
        <v-select
          v-if="filterOptions.length"
          v-model="selectedFilter"
          :items="filterOptions"
          item-title="label"
          item-value="value"
          return-object
          label="Filter"
          variant="outlined"
          density="compact"
          rounded="lg"
          clearable
          chips
          hide-details
          class="toolbar-select"
          @update:modelValue="applyFilters"
        />

        <!-- SORT -->
        <v-select
          v-if="sortOptions.length"
          v-model="selectedSort"
          :items="sortOptions"
          item-title="label"
          item-value="value"
          return-object
          label="Sort"
          variant="outlined"
          density="compact"
          rounded="lg"
          clearable
          chips
          hide-details
          class="toolbar-select"
          @update:modelValue="applyFilters"
        />
      </v-col>

      <v-col cols="3" class="d-flex justify-end align-center">
        <!-- Export -->
         <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="outlined"
              size="small"
              class="toolbar-icon-btn export-btn"
            >
              <v-icon size="18">mdi-export-variant</v-icon>

              <v-tooltip activator="parent" location="top">
                Export
              </v-tooltip>
            </v-btn>
          </template>

            <v-list density="compact">
              <v-list-item @click="$emit('export', 'pdf')">
                <template #prepend>
                  <v-icon>mdi-file-pdf-box</v-icon>
                </template>

                <v-list-item-title>Download in PDF</v-list-item-title>
              </v-list-item>

              <v-list-item @click="$emit('export', 'excel')">
                <template #prepend>
                  <v-icon>mdi-file-excel</v-icon>
                </template>

                <v-list-item-title>Download in Excel</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>    
        <!-- Reset -->
        <v-btn
          icon
          variant="outlined"
          size="small"
          class="toolbar-icon-btn reset-btn ml-2"
          @click="handleReset"
        >
          <v-icon size="18" :class="{ rotate: loading }"> mdi-refresh </v-icon>

          <v-tooltip activator="parent" location="top">
            Reset Filters
          </v-tooltip>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useUiStore } from "@/store";
import { mapState } from "pinia";

export default {
  name: "ActionToolbar",

  props: {
    filterOptions: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    searchOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedSort: null,
      selectedFilter: null,
      searchValue: "",
      selectedSearchField: this.searchOptions.find((option) => option.default) || {
        label: "Name",
        value: "name",
      },
      dateMenu: false,
      selectedDate: null,
      dateAllowedRoutes: ["Report"],
    };
  },

  computed: {
    ...mapState(useUiStore, { loading: "getLoading"}),
  },

  methods: {
    handleReset() {
      this.searchValue = "";
      this.selectedSort = null;
      this.selectedFilter = null;
      this.selectedDate = null;

      this.$emit("filter", {});
    },
    onDateSelect(date) {
      this.selectedDate = new Date(date).toLocaleDateString("en-CA"); 
      this.dateMenu = false;
    },
    isDateAllowed() {
      return this.dateAllowedRoutes.includes(this.$route.name);
    },
    applyFilters() {
      const filters = {};

      if (this.selectedSearchField.value && this.searchValue) {
        filters[this.selectedSearchField.value] = this.searchValue;
      }
      if (this.selectedFilter?.key && this.selectedFilter?.value) {
        filters[this.selectedFilter.key] = this.selectedFilter.value;
      }
      if (this.selectedSort?.key && this.selectedSort?.value) {
        filters[this.selectedSort.key] = this.selectedSort.value;
      }
      if (this.selectedDate) {
        filters.date = this.selectedDate;
      }
      this.$emit("filter", { filters });
    },
  },
};
</script>

<style scoped>
.toolbar-wrapper {
  border-radius: 16px;
  padding: 18px;
}
.search-wrapper {
  height: 42px;
  display: flex;
  align-items: center;
  border: 1px solid #dcdcdc;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.25s ease;
  min-width: 350px;
  max-width: 425px;
  width: 100%;
}
.search-wrapper:hover {
  border-color: #f59e0b;
}
.search-wrapper:focus-within {
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12);
}
.search-type-selector {
  min-width: 130px;
  height: 100%;
  border-right: 1px solid #ececec;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 500;
  color: #616161;
}
.search-input {
  flex: 1;
}
:deep(.search-input .v-field) {
  box-shadow: none !important;
  background: transparent !important;
}
:deep(.search-input .v-field__input) {
  padding: 0 10px !important;
}
:deep(.search-input .v-field__outline) {
  display: none;
}
:deep(.search-input input) {
  font-size: 13.5px;
  padding-left: 12px;
}

/* SEARCH BUTTON */
.search-btn {
  border-radius: 0 !important;
  width: 70px !important;
  height: 42px !important;
}
/* SELECT */
.toolbar-select {
  max-width: 250px;
  min-width: 180px;
}
:deep(.radio-small .v-label) {
  font-size: 13px;
  line-height: 1.2;
}
:deep(.v-field-label) {
  font-size: 12px;
}
:deep(.v-chip) {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}
:deep(.v-chip__close) {
  color: #1976d2;
}
.toolbar-icon-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 8px !important;
  border: 1px solid #dfe3e8 !important;
  background: #ffffff;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, transform 0.2s ease;
}

/* Export */
.export-btn {
  color: #596273 !important;
}

.export-btn:hover {
  color: #1976d2 !important;
  border-color: #90caf9 !important;
  background: rgba(33, 150, 243, 0.06);
}

/* Reset */
.reset-btn {
  color: #69717d !important;
}

.reset-btn:hover {
  color: #e53935 !important;
  border-color: #ef9a9a !important;
  background: rgba(244, 67, 54, 0.06);
}

/* Small hover movement */
.toolbar-icon-btn:hover {
  transform: translateY(-1px);
}

/* Loading rotation */
.rotate {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>